import type {
  ProductSelectionBlockV2,
  ProductSelectShade,
} from "@lib/config/types";
import { type IndividualProduct } from "@lib/products";
import { resolveFontFamilyVariables, type FontFamily } from "@lib/utils";
import { useEffect, useMemo, type CSSProperties } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { SelectedShade } from "../products/selected-shade";
import { type ProductSelectProps } from "./product-select-v2";
import { ShadeSelectorField } from "./shade-selector-field";
import { ShadeSelectorFieldWithGroups } from "./shade-selector-field-with-groups";

export function useSelectedShade(props: ProductSelectProps) {
  const { control, setValue } = useFormContext();
  const { product_selection: productSelect, activity } = props;

  if (
    activity.products.type === "single" ||
    activity.products.type === "bundle"
  )
    return { selectedShade: null, defaultAllocationId: null };

  const products = activity.products.product.products;

  const { layout } = productSelect;

  const defaultAllocationId = useMemo(() => {
    if (getHasShade(layout) && products) {
      return (
        products.find(({ hasStock }) => hasStock)?.allocationIds.join(",") ?? ""
      );
    }
    return "";
  }, [layout, products]);

  useEffect(() => {
    setValue("order_allocation_ids", defaultAllocationId);
  }, [products, setValue]);

  const selectedAllocationId = useWatch({
    control,
    name: "order_allocation_ids",
    defaultValue: defaultAllocationId,
  }) as string;

  const selectedShade = products.find(
    (product) => product.allocationIds.join(",") === selectedAllocationId,
  ) as IndividualProduct;

  return { selectedShade, defaultAllocationId };
}

export function getHasShade(
  layout: ProductSelectionBlockV2["product_selection"]["layout"],
): layout is {
  shade: ProductSelectShade;
} {
  return typeof layout === "object" && layout !== null && "shade" in layout;
}

export function ShadeSelector(props: ProductSelectProps) {
  const { product_selection: productSelect } = props;

  const { layout, out_of_stock_message } = productSelect;

  const { selectedShade } = useSelectedShade(props);

  if (!selectedShade) return null;

  const shadeTitleStyle = getHasShade(layout)
    ? {
        "--color": layout.shade.shade_name.color,
        ...resolveFontFamilyVariables(
          "--shade-title",
          layout.shade.shade_name.font as FontFamily,
          layout.shade.shade_name.font_properties,
        ),
        fontStyle: "var(--shade-title-font-style)",
        fontWeight: "var(--shade-title-font-weight)",
      }
    : ({} as CSSProperties);

  const categorise =
    getHasShade(layout) &&
    layout.shade !== null &&
    layout.shade.show_in_groups === true;

  return (
    <>
      <SelectedShade
        shade={selectedShade}
        out_of_stock_message={out_of_stock_message}
        style={shadeTitleStyle as CSSProperties}
      />
      {categorise ? (
        <ShadeSelectorFieldWithGroups {...props} />
      ) : (
        <ShadeSelectorField {...props} />
      )}
    </>
  );
}
